/** Small colour helpers (sRGB, WCAG 2.x contrast). */

export const HEX_RE = /^#[0-9A-F]{6}$/;

export function normalizeHex(input: string): string | null {
  const s = input.trim().toUpperCase();
  if (HEX_RE.test(s)) return s;
  const short = /^#([0-9A-F])([0-9A-F])([0-9A-F])$/.exec(s);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return null;
}

function channels(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  const h = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

/** Linear blend: t = 0 gives `a`, t = 1 gives `b`. */
export function mix(a: string, b: string, t: number): string {
  const ca = channels(a);
  const cb = channels(b);
  return toHex([ca[0] + (cb[0] - ca[0]) * t, ca[1] + (cb[1] - ca[1]) * t, ca[2] + (cb[2] - ca[2]) * t]);
}

export function luminance(hex: string): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = channels(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The first candidate that reaches `min` contrast against `bg`; otherwise the
 * best of white, near-black and black. One of white and black always reaches
 * at least 4.58:1, whatever the background.
 */
export function readableOn(bg: string, candidates: string[], min = 4.5): string {
  for (const c of [...candidates, '#FFFFFF', '#111111']) if (contrast(c, bg) >= min) return c;
  return contrast('#FFFFFF', bg) >= contrast('#000000', bg) ? '#FFFFFF' : '#000000';
}

/** A darker (or, for very dark colours, lighter) edge colour for outlines. */
export function rimOf(hex: string): string {
  return luminance(hex) < 0.03 ? mix(hex, '#FFFFFF', 0.28) : mix(hex, '#000000', 0.38);
}
